import z from "zod/v4";
import type { $ZodErrorTree } from "zod/v4/core";
import { type Segment } from "../types";
import type { SegmentManagerAction, SegmentReducerState } from "./actions";

const DEFAULT_GROUP = {
  type: "segment_group" as const,
  operation: "AND" as const,
  recipient_type_key: "",
  conditions: [
    {
      field: "",
      operation: "IS" as const,
      values: [],
      invert: false,
    },
  ],
};

export function segmentReducer<T extends Segment>(
  state: SegmentReducerState<T>,
  action: SegmentManagerAction<T>,
): SegmentReducerState<T> {
  let newState = state.segment;

  switch (action.type) {
    case "RESET":
      return action.payload;
    case "SET_SCHEMA":
      return {
        ...state,
        schema: action.payload,
        errors: {
          display: state.errors.display,
          errors: validateSegment(state.segment, action.payload),
        },
      };
    case "CLEAR_SAVE_ERROR":
      return {
        ...state,
        saveState: { saving: false, error: null },
      };
    case "SET_SAVING":
      return {
        ...state,
        saveState: { saving: true, error: null },
      };
    case "FINISHED_SAVING":
      return {
        ...state,
        saveState: { saving: false, error: action.payload },
      };
    case "SET_NAME":
      newState = { ...newState, name: action.payload };
      break;
    case "SET_DESCRIPTION":
      newState = { ...newState, description: action.payload };
      break;
    case "SET_OPERATION":
      newState = { ...newState, operation: action.payload };
      break;
    case "ADD_GROUP":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: [...newState.segment_json.segments, ...(action.payload ?? [DEFAULT_GROUP])],
        },
      };
      break;
    case "REMOVE_GROUP":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: newState.segment_json.segments.filter((_, i) => i !== action.payload),
        },
      };
      break;
    case "UPDATE_GROUP_OPERATION":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: newState.segment_json.segments.map((g, i) =>
            i === action.payload.index ? { ...g, operation: action.payload.operation } : g,
          ),
        },
      };
      break;
    case "UPDATE_GROUP_AUDIENCE":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: newState.segment_json.segments.map((g, i) =>
            i === action.payload.index
              ? { ...g, recipient_type_key: action.payload.recipient_type_key }
              : g,
          ),
        },
      };
      break;

    case "ADD_CONDITION":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: newState.segment_json.segments.map((g, i) =>
            i === action.payload.groupIndex
              ? {
                  ...g,
                  conditions: [
                    ...g.conditions,
                    { field: "", invert: false, operation: "IS", values: [] },
                  ],
                }
              : g,
          ),
        },
      };
      break;
    case "REMOVE_CONDITION":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: newState.segment_json.segments.map((g, i) =>
            i === action.payload.groupIndex
              ? {
                  ...g,
                  conditions: g.conditions.filter((_, j) => j !== action.payload.conditionIndex),
                }
              : g,
          ),
        },
      };
      break;
    case "UPDATE_CONDITION":
      newState = {
        ...newState,
        segment_json: {
          ...newState.segment_json,
          segments: newState.segment_json.segments.map((g, i) =>
            i === action.payload.groupIndex
              ? {
                  ...g,
                  conditions: g.conditions.map((c, j) =>
                    j === action.payload.conditionIndex ? action.payload.condition : c,
                  ),
                }
              : g,
          ),
        },
      };
      break;
    case "SET_ERRORS_DISPLAY":
      return {
        ...state,
        errors: { ...state.errors, display: action.payload },
      };
  }

  return {
    ...state,
    segment: newState,
    errors: {
      display: state.errors.display,
      errors: validateSegment(newState, state.schema),
    },
  };
}

export function validateSegment<T extends Segment>(
  segment: Segment,
  schema: z.ZodType<T>,
): $ZodErrorTree<T> | null {
  const { success, error } = schema.safeParse(segment);
  if (success) return null;

  return z.treeifyError(error);
}

export function makeDefaultState<T extends Segment>(
  schema: z.ZodType<T>,
  segment: T,
): SegmentReducerState<T> {
  return {
    schema,
    saveState: { saving: false, error: null },
    errors: {
      errors: validateSegment(segment, schema),
      display: false,
    },
    segment,
  };
}
